import { Platform } from "react-native";

/**
 * Web-only background-color sampler.
 *
 * Colour-extraction libraries return the *dominant* / most-vivid colour, which
 * for something like a yellow Batman logo on a black field is the yellow — not
 * the background. The background almost always lives along the image's edges,
 * so we sample the border ring and return its most common colour instead. This
 * makes a card's backdrop blend with the image's real background.
 *
 * Returns `null` when it can't sample (non-web, image failed to load, or the
 * canvas is CORS-tainted) so callers can fall back to another strategy.
 */
export async function getEdgeBackgroundColor(
  uri: string,
): Promise<string | null> {
  if (Platform.OS !== "web" || typeof document === "undefined") return null;

  return new Promise((resolve) => {
    const img = new window.Image();
    // Required so the server's CORS headers let us read the pixels; without a
    // CORS-enabled response getImageData throws and we fall back below.
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return resolve(null);

        // Downscale — edge colours don't need full resolution and this keeps it
        // fast on large images.
        const scale = Math.min(1, 64 / Math.max(w, h));
        const cw = Math.max(2, Math.round(w * scale));
        const ch = Math.max(2, Math.round(h * scale));

        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, cw, ch);

        const { data } = ctx.getImageData(0, 0, cw, ch);

        // Tally edge pixels into coarse colour buckets (4-bit per channel) so
        // anti-aliasing/noise collapses onto the true background bucket.
        const buckets = new Map<
          string,
          { n: number; r: number; g: number; b: number }
        >();

        const tally = (x: number, y: number) => {
          const i = (y * cw + x) * 4;
          if (data[i + 3] < 200) return; // skip (semi-)transparent pixels
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
          const cur = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
          cur.n += 1;
          cur.r += r;
          cur.g += g;
          cur.b += b;
          buckets.set(key, cur);
        };

        for (let x = 0; x < cw; x++) {
          tally(x, 0);
          tally(x, ch - 1);
        }
        for (let y = 0; y < ch; y++) {
          tally(0, y);
          tally(cw - 1, y);
        }

        let best: { n: number; r: number; g: number; b: number } | null = null;
        for (const v of buckets.values()) {
          if (!best || v.n > best.n) best = v;
        }
        if (!best) return resolve(null);

        const r = Math.round(best.r / best.n);
        const g = Math.round(best.g / best.n);
        const b = Math.round(best.b / best.n);
        resolve(`rgb(${r}, ${g}, ${b})`);
      } catch {
        // Tainted canvas (image served without CORS) or any other failure.
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = uri;
  });
}
